/**
 * Sistemi i Alarmeve – konfigurim & historik për pajisjet S475 (DIO IOT).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

import { BACKEND_URL } from '../config';

const SEVERITY_COLORS = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  MEDIUM: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const SEVERITY_ACCENT = {
  LOW: { border: 'border-l-slate-500', grad: 'from-slate-100/80 via-white to-white dark:from-slate-800/60 dark:via-gray-900/80 dark:to-gray-900' },
  MEDIUM: { border: 'border-l-amber-500', grad: 'from-amber-50/90 via-white to-white dark:from-amber-900/25 dark:via-gray-900/90 dark:to-gray-900' },
  HIGH: { border: 'border-l-orange-500', grad: 'from-orange-50/90 via-white to-white dark:from-orange-900/25 dark:via-gray-900/90 dark:to-gray-900' },
  CRITICAL: { border: 'border-l-red-500', grad: 'from-red-50/90 via-white to-white dark:from-red-900/20 dark:via-gray-900/90 dark:to-gray-900' },
};

const emptyRules = () => ({
  inputs: Array.from({ length: 5 }, (_, i) => ({ index: i, enabled: false, window_minutes: 2 })),
  outputs: Array.from({ length: 4 }, (_, i) => ({ index: i, enabled: false, window_minutes: 2 })),
  phases: {
    L1: { min_enabled: false, min_value: '', max_enabled: false, max_value: '', loss_enabled: false },
    L2: { min_enabled: false, min_value: '', max_enabled: false, max_value: '', loss_enabled: false },
    L3: { min_enabled: false, min_value: '', max_enabled: false, max_value: '', loss_enabled: false },
  },
});

const Alarms = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('active');
  const [s475Events, setS475Events] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);
  const [filterDeviceId, setFilterDeviceId] = useState('');
  const [s475Devices, setS475Devices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifForm, setNotifForm] = useState({ email: '', phone: '', company_id: '' });
  const [companies, setCompanies] = useState([]);
  const [configDeviceId, setConfigDeviceId] = useState('');
  const [rulesDraft, setRulesDraft] = useState(emptyRules);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportFilterDeviceId, setReportFilterDeviceId] = useState('');
  const [reportEvents, setReportEvents] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchS475Devices = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
      setS475Devices(Array.isArray(res.data) ? res.data : []);
    } catch {
      setS475Devices([]);
    }
  }, []);

  useEffect(() => {
    fetchS475Devices();
  }, [fetchS475Devices]);

  const fetchS475Events = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterActiveOnly) params.set('active_only', 'true');
      if (filterDeviceId) params.set('device_id', filterDeviceId);
      const res = await axios.get(`${BACKEND_URL}/api/s475/alarm-events?${params}`, { headers: headers() });
      setS475Events(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('S475 alarm events:', e);
      setS475Events([]);
    } finally {
      setLoading(false);
    }
  }, [filterActiveOnly, filterDeviceId]);

  useEffect(() => {
    if (activeSection === 'active' || activeSection === 'historical') {
      const hist = activeSection === 'historical';
      if (hist) {
        setLoading(true);
        (async () => {
          try {
            const params = new URLSearchParams();
            params.set('active_only', 'false');
            if (filterDeviceId) params.set('device_id', filterDeviceId);
            const res = await axios.get(`${BACKEND_URL}/api/s475/alarm-events?${params}`, { headers: headers() });
            setS475Events(Array.isArray(res.data) ? res.data : []);
          } catch {
            setS475Events([]);
          } finally {
            setLoading(false);
          }
        })();
      } else {
        fetchS475Events();
      }
    }
  }, [activeSection, filterActiveOnly, filterDeviceId, fetchS475Events]);

  useEffect(() => {
    if (activeSection === 'notifications') {
      fetchNotifications();
      fetchCompanies();
    }
  }, [activeSection]);

  const loadRulesForDevice = async (deviceId) => {
    if (!deviceId) {
      setRulesDraft(emptyRules());
      return;
    }
    setConfigLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/s475/devices/${deviceId}/alarm-rules`, { headers: headers() });
      const r = res.data || {};
      const rawIn = Array.isArray(r.inputs) ? r.inputs : [];
      const inputs = emptyRules().inputs.map((_, i) => ({
        index: i,
        enabled: !!(rawIn[i] && rawIn[i].enabled),
        window_minutes: Number(rawIn[i] && rawIn[i].window_minutes) || 2,
      }));
      const outputs = (r.outputs || emptyRules().outputs).map((row, i) => ({
        index: i,
        enabled: !!row.enabled,
        window_minutes: Number(row.window_minutes) || 2,
      }));
      const phases = { ...emptyRules().phases };
      ['L1', 'L2', 'L3'].forEach((ph) => {
        const p = (r.phases && r.phases[ph]) || {};
        phases[ph] = {
          min_enabled: !!p.min_enabled,
          min_value: p.min_value != null && p.min_value !== '' ? String(p.min_value) : '',
          max_enabled: !!p.max_enabled,
          max_value: p.max_value != null && p.max_value !== '' ? String(p.max_value) : '',
          loss_enabled: !!p.loss_enabled,
        };
      });
      setRulesDraft({ inputs, outputs, phases });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
      setRulesDraft(emptyRules());
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'config' && configDeviceId) {
      loadRulesForDevice(configDeviceId);
    }
  }, [activeSection, configDeviceId]);

  const saveRules = async () => {
    if (!configDeviceId) {
      toast.error(t('selectDevice') || 'Zgjidh pajisjen');
      return;
    }
    setSavingRules(true);
    try {
      const phases = {};
      ['L1', 'L2', 'L3'].forEach((ph) => {
        const p = rulesDraft.phases[ph];
        const mn = p.min_value === '' ? null : parseFloat(p.min_value);
        const mx = p.max_value === '' ? null : parseFloat(p.max_value);
        phases[ph] = {
          min_enabled: p.min_enabled,
          min_value: mn != null && !Number.isNaN(mn) ? mn : null,
          max_enabled: p.max_enabled,
          max_value: mx != null && !Number.isNaN(mx) ? mx : null,
          loss_enabled: p.loss_enabled,
        };
      });
      await axios.put(
        `${BACKEND_URL}/api/s475/devices/${configDeviceId}/alarm-rules`,
        {
          inputs: rulesDraft.inputs,
          outputs: rulesDraft.outputs,
          phases,
        },
        { headers: headers() }
      );
      toast.success(t('alarmRulesSaved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('error'));
    } finally {
      setSavingRules(false);
    }
  };

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/alarm-notifications`, { headers: headers() });
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/companies`, { headers: headers() });
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCompanies([]);
    }
  };

  const loadMonthReport = useCallback(async () => {
    if (!reportMonth || reportMonth.length < 7) return;
    setReportLoading(true);
    try {
      const [y, m] = reportMonth.split('-').map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      const pad = (n) => String(n).padStart(2, '0');
      const dateFrom = `${y}-${pad(m)}-01`;
      const dateTo = `${y}-${pad(m)}-${pad(lastDay)}`;
      const params = new URLSearchParams({
        active_only: 'false',
        limit: '5000',
        date_from: dateFrom,
        date_to: dateTo,
      });
      if (reportFilterDeviceId) params.set('device_id', reportFilterDeviceId);
      const res = await axios.get(`${BACKEND_URL}/api/s475/alarm-events?${params}`, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setReportEvents(list);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || e.message || t('error'));
      setReportEvents([]);
    } finally {
      setReportLoading(false);
    }
  }, [reportMonth, reportFilterDeviceId, t]);

  useEffect(() => {
    if (activeSection === 'reports') {
      loadMonthReport();
    }
  }, [activeSection, reportMonth, reportFilterDeviceId, loadMonthReport]);

  const downloadAlarmReportCsv = () => {
    if (!reportEvents.length) return;
    const bom = '\uFEFF';
    const head = [t('alarmCsvSeverity'), t('alarmCsvMessage'), t('locationLabel'), t('alarmCsvRule'), t('dateAndTime'), t('acknowledged')];
    const lines = [
      head,
      ...reportEvents.map((a) => [
        a.severity || '',
        (a.message || a.rule_key || '').replace(/"/g, '""'),
        (deviceLabel(a.device_id) || '').replace(/"/g, '""'),
        a.rule_key || '',
        formatDate(a.timestamp),
        a.acknowledged ? t('alarmCsvAckYes') : t('alarmCsvAckNo'),
      ]),
    ];
    const csv = bom + lines.map((r) => r.map((x) => `"${String(x)}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `alarme-${reportMonth}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const acknowledgeAlarm = async (alarmId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/s475/alarm-events/${alarmId}/acknowledge`, {}, { headers: headers() });
      fetchS475Events();
      if (activeSection === 'historical') {
        const params = new URLSearchParams();
        params.set('active_only', 'false');
        if (filterDeviceId) params.set('device_id', filterDeviceId);
        const res = await axios.get(`${BACKEND_URL}/api/s475/alarm-events?${params}`, { headers: headers() });
        setS475Events(Array.isArray(res.data) ? res.data : []);
      }
      if (activeSection === 'reports') {
        loadMonthReport();
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || t('error'));
    }
  };

  const submitNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.email?.trim()) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/alarm-notifications`,
        { email: notifForm.email?.trim(), company_id: notifForm.company_id || undefined },
        { headers: headers() }
      );
      setNotifForm({ email: '', company_id: '' });
      fetchNotifications();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('registerError'));
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/alarm-notifications/${id}`, { headers: headers() });
      fetchNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const sections = useMemo(() => {
    const all = [
      { id: 'active', labelKey: 'activeAlarms', icon: '✓', descKey: 'activeAlarmsList' },
      { id: 'config', labelKey: 'alarmTabConfig', icon: '⚙', noDesc: true },
      { id: 'historical', labelKey: 'history', icon: '📊', descKey: 'alarmHistory' },
      { id: 'notifications', labelKey: 'notifications', icon: '🔔', descKey: 'registerNotif' },
      { id: 'reports', labelKey: 'reportsSection', icon: '📄', descKey: 'alarmReports' },
    ];
    const r = user?.role;
    return all.filter((s) => {
      if (s.id === 'config' && r !== 'SUPERADMIN') return false;
      if (s.id === 'notifications' && r === 'VIEWER') return false;
      return true;
    });
  }, [user?.role]);

  useEffect(() => {
    const r = user?.role;
    if (activeSection === 'config' && r !== 'SUPERADMIN') setActiveSection('active');
    else if (activeSection === 'notifications' && r === 'VIEWER') setActiveSection('active');
  }, [user?.role, activeSection]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
  };

  const deviceLabel = (id) => {
    const d = s475Devices.find((x) => String(x.id) === String(id));
    return d?.location_name || d?.label || id || '—';
  };

  const updateInput = (idx, field, value) => {
    setRulesDraft((prev) => {
      const inputs = [...prev.inputs];
      inputs[idx] = { ...inputs[idx], [field]: value };
      return { ...prev, inputs };
    });
  };

  const updateOutput = (idx, field, value) => {
    setRulesDraft((prev) => {
      const outputs = [...prev.outputs];
      outputs[idx] = { ...outputs[idx], [field]: value };
      return { ...prev, outputs };
    });
  };

  const updatePhase = (ph, field, value) => {
    setRulesDraft((prev) => ({
      ...prev,
      phases: {
        ...prev.phases,
        [ph]: { ...prev.phases[ph], [field]: value },
      },
    }));
  };

  const renderEventList = (showAck) => (
    <ul className="space-y-4">
      {s475Events.map((a) => {
        const sev = a.severity || 'MEDIUM';
        const acc = SEVERITY_ACCENT[sev] || SEVERITY_ACCENT.MEDIUM;
        return (
          <li
            key={a.id}
            className={`group relative overflow-hidden rounded-2xl border border-gray-200/90 dark:border-gray-600/70 bg-gradient-to-br ${acc.grad} shadow-sm hover:shadow-lg transition-shadow duration-200 border-l-[5px] ${acc.border}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-stretch">
              <div className="flex-1 p-4 sm:p-5 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold tracking-wide ${SEVERITY_COLORS[sev] || SEVERITY_COLORS.MEDIUM}`}>
                    {a.severity}
                  </span>
                  {a.is_active && (
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-100/80 dark:bg-red-900/40 px-2 py-0.5 rounded-md">
                      {t('alarmActiveBadge')}
                    </span>
                  )}
                  {a.acknowledged && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{t('acknowledged')}</span>
                  )}
                </div>
                <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-snug">
                  {a.message || a.rule_key}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-base opacity-80" aria-hidden>📍</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{deviceLabel(a.device_id)}</span>
                  </span>
                  <span className="text-gray-400 dark:text-gray-600" aria-hidden>
                    |
                  </span>
                  <code className="text-[11px] sm:text-xs px-2 py-0.5 rounded bg-gray-100/80 dark:bg-gray-800/80 text-cyan-800 dark:text-cyan-200 font-mono">
                    {a.rule_key}
                  </code>
                  <span className="text-gray-400 dark:text-gray-600" aria-hidden>
                    |
                  </span>
                  <time className="tabular-nums text-gray-500 dark:text-gray-400">{formatDate(a.timestamp)}</time>
                </div>
              </div>
              {showAck && a.is_active && !a.acknowledged && (
                <div className="flex sm:flex-col justify-end p-4 sm:p-5 pt-0 sm:pt-5 sm:pl-0 sm:border-l border-gray-200/80 dark:border-gray-600/50">
                  <button
                    type="button"
                    onClick={() => acknowledgeAlarm(a.id)}
                    className="w-full sm:w-auto shrink-0 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold shadow-md"
                  >
                    {t('acknowledge')}
                  </button>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="p-4 md:p-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">{t('alarmSystem')}</h1>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-6">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSection(s.id)}
                className={`rounded-xl p-4 md:p-5 text-left transition border-2 ${
                  activeSection === s.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="text-2xl md:text-3xl mb-2 block" aria-hidden>
                  {s.icon}
                </span>
                <span className="font-semibold text-sm md:text-base block">{s.labelKey ? t(s.labelKey) : s.label}</span>
                {!s.noDesc && (s.descKey || s.desc) && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden md:block line-clamp-3">
                    {s.descKey ? t(s.descKey) : s.desc}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="overflow-hidden">
            {(activeSection === 'active' || activeSection === 'historical') && (
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  {activeSection === 'active' && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={filterActiveOnly}
                        onChange={(e) => setFilterActiveOnly(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('activeOnly')}</span>
                    </label>
                  )}
                  <select
                    value={filterDeviceId}
                    onChange={(e) => setFilterDeviceId(e.target.value)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 max-w-xs"
                  >
                    <option value="">{t('allDevicesFilter')}</option>
                    {s475Devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.location_name || d.label || d.id}
                      </option>
                    ))}
                  </select>
                </div>
                {loading ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('loading')}</p>
                ) : s475Events.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('noAlarms')}</p>
                ) : (
                  renderEventList(activeSection === 'active')
                )}
              </div>
            )}

            {activeSection === 'config' && (
              <div className="p-4 md:p-6 space-y-6 font-alarm antialiased">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-sm font-semibold tracking-wide text-slate-600 dark:text-slate-300 mb-1.5">{t('deviceLabel')}</label>
                    <select
                      value={configDeviceId}
                      onChange={(e) => setConfigDeviceId(e.target.value)}
                      className="rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm font-medium px-4 py-2.5 min-w-[220px] shadow-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                    >
                      <option value="">{t('selectDevice') || '—'}</option>
                      {s475Devices.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.location_name || d.label || d.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    disabled={!configDeviceId || savingRules}
                    onClick={saveRules}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold tracking-wide shadow-md shadow-emerald-900/20"
                  >
                    {savingRules ? t('loading') : t('saveAlarmRules')}
                  </button>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed max-w-3xl border-l-4 border-cyan-500/40 pl-3">{t('phaseLinNote')}</p>

                {configLoading ? (
                  <p className="text-slate-500 font-medium">{t('loading')}</p>
                ) : !configDeviceId ? (
                  <p className="text-slate-500 font-medium">{t('selectDevice') || 'Zgjidh një pajisje.'}</p>
                ) : (
                  <>
                    <div className="rounded-2xl border border-slate-200/90 dark:border-slate-600/50 bg-gradient-to-b from-slate-50/95 to-white dark:from-slate-900/50 dark:to-slate-800/90 p-5 md:p-6 shadow-sm">
                      <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-slate-200/80 dark:border-slate-600/50">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 text-sm font-bold ring-1 ring-cyan-500/25">DIN</span>
                        <div>
                          <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Input 1–5 (DIN0–DIN4)</h3>
                          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">{t('alarmCfgDinSubtitle')}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {rulesDraft.inputs.map((row, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-xl border border-slate-200/70 dark:border-slate-600/40 bg-white/80 dark:bg-slate-800/50 px-4 py-3.5 shadow-sm"
                          >
                            <label className="flex cursor-pointer items-center gap-3 min-w-0 flex-1">
                              <input
                                type="checkbox"
                                checked={row.enabled}
                                onChange={(e) => updateInput(idx, 'enabled', e.target.checked)}
                                className="h-4 w-4 shrink-0 rounded border-2 border-slate-300 dark:border-slate-500 text-cyan-600 focus:ring-2 focus:ring-cyan-500/40"
                              />
                              <span className="text-[15px] font-bold text-slate-800 dark:text-slate-100">Input {idx + 1}</span>
                              <span className="rounded-md bg-slate-200/90 dark:bg-slate-700 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">DIN{idx}</span>
                            </label>
                            <div className="flex items-center justify-end gap-2 sm:shrink-0">
                              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('windowMinutesShort')}</span>
                              <input
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={row.window_minutes}
                                onChange={(e) => updateInput(idx, 'window_minutes', parseFloat(e.target.value) || 2)}
                                className="w-24 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/25"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/90 dark:border-slate-600/50 bg-gradient-to-b from-violet-50/60 to-white dark:from-violet-950/20 dark:to-slate-800/90 p-5 md:p-6 shadow-sm">
                      <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-slate-200/80 dark:border-slate-600/50">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-700 dark:text-violet-300 text-sm font-bold ring-1 ring-violet-500/25">DO</span>
                        <div>
                          <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Output 0–3 (DO)</h3>
                          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">{t('alarmCfgDoSubtitle')}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {rulesDraft.outputs.map((row, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-xl border border-slate-200/70 dark:border-slate-600/40 bg-white/80 dark:bg-slate-800/50 px-4 py-3.5 shadow-sm"
                          >
                            <label className="flex cursor-pointer items-center gap-3 min-w-0 flex-1">
                              <input
                                type="checkbox"
                                checked={row.enabled}
                                onChange={(e) => updateOutput(idx, 'enabled', e.target.checked)}
                                className="h-4 w-4 shrink-0 rounded border-2 border-slate-300 dark:border-slate-500 text-violet-600 focus:ring-2 focus:ring-violet-500/40"
                              />
                              <span className="text-[15px] font-bold text-slate-800 dark:text-slate-100">DO{idx}</span>
                            </label>
                            <div className="flex items-center justify-end gap-2 sm:shrink-0">
                              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t('windowMinutesShort')}</span>
                              <input
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={row.window_minutes}
                                onChange={(e) => updateOutput(idx, 'window_minutes', parseFloat(e.target.value) || 2)}
                                className="w-24 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/25"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/90 dark:border-slate-600/50 bg-gradient-to-b from-amber-50/40 to-white dark:from-amber-950/15 dark:to-slate-800/90 p-5 md:p-6 shadow-sm">
                      <div className="mb-5 pb-4 border-b border-slate-200/80 dark:border-slate-600/50">
                        <h3 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">L1, L2, L3 (lin)</h3>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">{t('alarmCfgPhaseBlockSubtitle')}</p>
                      </div>
                      <div className="space-y-4">
                        {['L1', 'L2', 'L3'].map((ph) => {
                          const p = rulesDraft.phases[ph];
                          return (
                            <div
                              key={ph}
                              className="overflow-hidden rounded-xl border border-slate-200/80 dark:border-slate-600/50 bg-slate-50/80 dark:bg-slate-800/40"
                            >
                              <div className="border-l-[5px] border-amber-500 bg-gradient-to-r from-amber-100/50 to-transparent dark:from-amber-900/25 dark:to-transparent px-4 py-2.5">
                                <span className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">{ph}</span>
                              </div>
                              <div className="grid gap-3 p-4 sm:grid-cols-3 sm:items-end">
                                <label className="flex flex-col gap-2 rounded-lg border border-slate-200/70 bg-white/90 dark:border-slate-600/50 dark:bg-slate-900/30 p-3">
                                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    <input
                                      type="checkbox"
                                      checked={p.min_enabled}
                                      onChange={(e) => updatePhase(ph, 'min_enabled', e.target.checked)}
                                      className="h-4 w-4 rounded border-2 border-slate-300 text-amber-600 focus:ring-amber-500/40"
                                    />
                                    {t('minVoltageShort')}
                                  </span>
                                  <input
                                    type="number"
                                    step="any"
                                    value={p.min_value}
                                    onChange={(e) => updatePhase(ph, 'min_value', e.target.value)}
                                    placeholder="kW"
                                    className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                  />
                                </label>
                                <label className="flex flex-col gap-2 rounded-lg border border-slate-200/70 bg-white/90 dark:border-slate-600/50 dark:bg-slate-900/30 p-3">
                                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    <input
                                      type="checkbox"
                                      checked={p.max_enabled}
                                      onChange={(e) => updatePhase(ph, 'max_enabled', e.target.checked)}
                                      className="h-4 w-4 rounded border-2 border-slate-300 text-amber-600 focus:ring-amber-500/40"
                                    />
                                    {t('maxVoltageShort')}
                                  </span>
                                  <input
                                    type="number"
                                    step="any"
                                    value={p.max_value}
                                    onChange={(e) => updatePhase(ph, 'max_value', e.target.value)}
                                    placeholder="kW"
                                    className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                  />
                                </label>
                                <label className="flex items-center gap-2 rounded-lg border border-slate-200/70 bg-white/90 dark:border-slate-600/50 dark:bg-slate-900/30 p-3 sm:min-h-[88px]">
                                  <input
                                    type="checkbox"
                                    checked={p.loss_enabled}
                                    onChange={(e) => updatePhase(ph, 'loss_enabled', e.target.checked)}
                                    className="h-4 w-4 shrink-0 rounded border-2 border-slate-300 text-amber-600 focus:ring-amber-500/40"
                                  />
                                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug">{t('phaseLossShort')}</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeSection === 'notifications' && (
              <div className="p-4 md:p-6">
                <form onSubmit={submitNotification} className="space-y-4 max-w-md mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('email')}</label>
                    <input
                      type="email"
                      value={notifForm.email}
                      onChange={(e) => setNotifForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder={t('emailPlaceholder')}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  {user?.role === 'SUPERADMIN' && companies.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('companyOptional')}</label>
                      <select
                        value={notifForm.company_id}
                        onChange={(e) => setNotifForm((f) => ({ ...f, company_id: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="">{t('all')}</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={!notifForm.email?.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium"
                  >
                    {t('registerNotifications')}
                  </button>
                </form>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('myRegistrations')}</h3>
                  {notifLoading ? (
                    <p className="text-gray-500 text-sm">{t('loading')}</p>
                  ) : notifications.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{t('noRegistrations')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {notifications.map((n) => (
                        <li key={n.id} className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm">
                          {n.email && <span className="text-gray-700 dark:text-gray-300">{n.email}</span>}
                          <button
                            type="button"
                            onClick={() => deleteNotification(n.id)}
                            className="ml-auto text-red-600 dark:text-red-400 hover:underline text-xs"
                          >
                            {t('delete')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'reports' && (
              <div className="p-4 md:p-6 space-y-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">{t('alarmReportsIntro')}</p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-slate-50/80 to-white dark:from-gray-800/50 dark:to-gray-900/80 p-4 md:p-5 shadow-inner">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('alarmReportSelectMonth')}</label>
                    <input
                      type="month"
                      value={reportMonth}
                      onChange={(e) => setReportMonth(e.target.value)}
                      className="rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('deviceLabel')}</label>
                    <select
                      value={reportFilterDeviceId}
                      onChange={(e) => setReportFilterDeviceId(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    >
                      <option value="">{t('allDevicesFilter')}</option>
                      {s475Devices.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.location_name || d.label || d.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadMonthReport()}
                    disabled={reportLoading}
                    className="sm:ml-auto px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold shadow"
                  >
                    {reportLoading ? t('loading') : t('alarmReportRefresh')}
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('alarmReportTotalCount')}: <span className="text-cyan-600 dark:text-cyan-400 tabular-nums">{reportEvents.length}</span>
                  </p>
                  {reportEvents.length > 0 && (
                    <button
                      type="button"
                      onClick={downloadAlarmReportCsv}
                      className="px-4 py-2 rounded-xl border-2 border-green-600 text-green-700 dark:text-green-400 dark:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 text-sm font-medium"
                    >
                      {t('alarmReportDownloadCsv')}
                    </button>
                  )}
                </div>
                {reportLoading ? (
                  <p className="text-gray-500 py-8">{t('loading')}</p>
                ) : reportEvents.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('noAlarms')}</p>
                ) : (
                  <ul className="space-y-4">
                    {reportEvents.map((a) => {
                      const sev = a.severity || 'MEDIUM';
                      const acc = SEVERITY_ACCENT[sev] || SEVERITY_ACCENT.MEDIUM;
                      return (
                        <li
                          key={`r-${a.id}`}
                          className={`overflow-hidden rounded-2xl border border-gray-200/90 dark:border-gray-600/70 bg-gradient-to-br ${acc.grad} shadow-sm border-l-[5px] ${acc.border}`}
                        >
                          <div className="p-4 sm:p-5">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${SEVERITY_COLORS[sev] || SEVERITY_COLORS.MEDIUM}`}>
                                {a.severity}
                              </span>
                              {a.acknowledged && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">{t('acknowledged')}</span>
                              )}
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-white">{a.message || a.rule_key}</p>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                              {deviceLabel(a.device_id)} · {a.rule_key} · {formatDate(a.timestamp)}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {t('alarmReportsHint')}
                </p>
                <Link
                  to="/reports"
                  className="inline-flex text-sm text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                >
                  {t('openReports')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Alarms;
